/**
 * @fileoverview WEB-676 / DPL-26 — Discovery v2 declared list tail.
 *
 * Copy is final per the developer handoff. Do not paraphrase the labels.
 *
 *   Remainder: `9 more in range tonight · NO TASTE SIGNAL YET · SHOWN IN TIME ORDER`
 *   Action:    `Show all 14`
 *   Widen:     `Widen the search → Next 7 days`
 *
 * The remainder is `in-range − head`, where the head is the set of rows the
 * list is currently showing:
 *
 *   - **Taste-matched head** (`meta.tasteMatchedTotal` is a number): the head
 *     is the matched population, so the remainder is `total − matched` and the
 *     list filters down to {@link isDiscoverListTailPrimaryRow}. A measured `0`
 *     matched total is a real remainder of `total` and MUST render.
 *   - **No taste signal** (`meta.tasteMatchedTotal` is `null` — an anonymous
 *     viewer, or an authenticated one who has not expressed a taste yet): there
 *     is no matched subset to hold back, so the head is simply the rows already
 *     loaded and the remainder is `total − loadedCount`. Nothing is filtered
 *     out of the list. WEB-791 — this is the case the city surface
 *     (`clt.<root>/discover`, always anonymous) is in on every cold load, and
 *     the reason it used to fall through to a bare "Load N more events →".
 *
 * Both branches read the SAME server totals the results bar reads, and neither
 * computes `total − tasteMatchedTotal` while that field is `null` (the arithmetic
 * the `meta.tasteMatchedTotal` contract forbids). `loadedCount` is a client-side
 * fact — how many rows are on screen — not a substituted `0`.
 *
 * The middle clause of the remainder line (`NO TASTE SIGNAL YET`) describes the
 * REMAINDER rows, not the viewer: those rows carry no taste match, which is why
 * they are shown in time order. It is accurate in both branches.
 */

import type { SponsoredEventMeta } from '@loci/types';

import { formatCount } from '@/lib/discover/formatCount';
import type { DateValue } from '@/lib/discover/url-state';

/** Row fields the tail's first-paint filter reads. */
export interface DiscoverListTailRow {
  readonly tasteMatchPct?: number | null;
  readonly sponsored?: SponsoredEventMeta;
}

export const LIST_TAIL_NO_TASTE_SIGNAL = 'NO TASTE SIGNAL YET';
export const LIST_TAIL_TIME_ORDER = 'SHOWN IN TIME ORDER';
export const LIST_TAIL_WIDEN_PROMPT = 'Widen the search';

export interface DiscoverListTailCounts {
  /** Corpus-wide in-range total (`meta.total`). */
  readonly total: number;
  /**
   * Corpus-wide taste-matched total (`meta.tasteMatchedTotal`).
   * `null` = FRE-917 gate failed or not measurable (anonymous viewer, or an
   * authenticated one with no taste signal yet).
   */
  readonly tasteMatchedTotal: number | null;
  /**
   * ORGANIC rows currently accumulated on screen. Only read on the
   * no-taste-signal branch, where it — not a matched total — is the head the
   * remainder counts against (WEB-791).
   *
   * Must exclude sponsored slots. `meta.total` counts the organic filtered set,
   * but the backend injects sponsored rows into the page AFTER pagination
   * without raising that total (`injectSponsoredSlots` returns
   * `[...capped, ...organic]` on the map surface and interleaves on the feed).
   * Passing a raw `events.length` therefore over-counts the head and can retire
   * the tail while organic events are still unloaded — e.g. 50 organic + 10
   * sponsored against `total: 55` reads as 60 and hides a remainder of 5.
   * Use {@link countOrganicRows}.
   */
  readonly loadedCount: number;
  /**
   * `true` when the viewer HAS a taste signal (`meta.personalized === true`)
   * but the corpus-wide matched count could not be measured — the backend
   * degrades `meta.tasteMatchedTotal` to `null` inside its own try/catch while
   * leaving per-event `tasteMatchPct` annotations in place.
   *
   * That state is NOT the no-signal branch: those rows can carry real taste
   * matches, so counting the remainder against loaded rows and captioning it
   * `NO TASTE SIGNAL YET` would state something false to a viewer who has one.
   * There is no honest remainder to declare, so the tail does not render and
   * pagination falls back to the load-more control.
   */
  readonly matchedTotalUnmeasurable?: boolean;
}

/** Row fields {@link countOrganicRows} reads. */
export interface DiscoverOrganicRow {
  readonly sponsored?: SponsoredEventMeta;
}

/**
 * Organic (non-sponsored) row count. Paid slots ride along in the page but are
 * not part of the population `meta.total` counts, so they must never move the
 * tail's arithmetic. See {@link DiscoverListTailCounts.loadedCount}.
 */
export function countOrganicRows(rows: readonly DiscoverOrganicRow[]): number {
  return rows.reduce((count, row) => (row.sponsored == null ? count + 1 : count), 0);
}

/**
 * Size of the head the list is currently showing: the taste-matched population
 * when one was measured, otherwise the organic rows already loaded.
 */
export function resolveDiscoverListTailHead(input: DiscoverListTailCounts): number {
  return input.tasteMatchedTotal ?? input.loadedCount;
}

/**
 * Honest remainder: in-range minus the head that is on screen. `0` once the
 * head covers everything in range — no tail, and nothing left to paginate.
 */
export function computeDiscoverListTailRemainder(input: DiscoverListTailCounts): number {
  return Math.max(0, input.total - resolveDiscoverListTailHead(input));
}

/**
 * True when the declared tail should render: a positive honest remainder, and a
 * state we can describe truthfully. A signal-bearing viewer whose corpus count
 * could not be measured gets no tail — see
 * {@link DiscoverListTailCounts.matchedTotalUnmeasurable}.
 */
export function shouldRenderDiscoverListTail(input: DiscoverListTailCounts): boolean {
  if (input.matchedTotalUnmeasurable === true) return false;
  return computeDiscoverListTailRemainder(input) > 0;
}

/**
 * True when the tail holds unmatched rows back behind "Show all" — i.e. only
 * when a taste-matched head exists to hold them back from. Without a taste
 * signal every loaded row IS the head, so filtering by
 * {@link isDiscoverListTailPrimaryRow} would empty the list (no row carries a
 * `tasteMatchPct`), which is the bug this predicate exists to prevent.
 */
export function shouldHoldBackUnmatchedRows(input: DiscoverListTailCounts): boolean {
  return input.tasteMatchedTotal !== null;
}

/**
 * Rows that stay visible while the declared tail is up. Taste-matched
 * organics and paid sponsored slots (WEB-36) remain; unmatched organics
 * wait behind "Show all".
 */
export function isDiscoverListTailPrimaryRow(event: DiscoverListTailRow): boolean {
  return typeof event.tasteMatchPct === 'number' || event.sponsored != null;
}

/**
 * Resolve the matched total the tail may subtract. Same honesty rule as
 * the results bar (WEB-667): a numeric `meta.tasteMatchedTotal` is only
 * usable when `meta.personalized === true` (FRE-917). Until the shared
 * query resolves, keep the SSR seed so first paint can show the tail;
 * once the query lands without the gate, force `null` so the tail cannot
 * disagree with an in-range-only results bar.
 */
export function resolveDiscoverListTailMatchedTotal(input: {
  readonly live: number | null | undefined;
  readonly initial: number | null | undefined;
  readonly personalized: boolean | undefined;
  readonly queryResolved: boolean;
}): number | null {
  if (input.personalized === true) {
    if (typeof input.live === 'number') return input.live;
    if (input.live === null) return null;
    return typeof input.initial === 'number' ? input.initial : null;
  }
  if (input.queryResolved) return null;
  return typeof input.initial === 'number' ? input.initial : null;
}

/**
 * True for the one state {@link resolveDiscoverListTailMatchedTotal} cannot
 * distinguish by its return value alone: the viewer HAS a taste signal
 * (`meta.personalized === true`) but the corpus-wide count came back `null`.
 *
 * Both that state and the anonymous one yield a `null` matched total, yet they
 * are opposites for the tail's copy — one viewer has taste matches on the page,
 * the other has none. Callers pass this through as
 * {@link DiscoverListTailCounts.matchedTotalUnmeasurable} so the tail can stay
 * silent rather than caption a signal-bearing viewer `NO TASTE SIGNAL YET`.
 */
export function isDiscoverListTailMatchedTotalUnmeasurable(input: {
  readonly personalized: boolean | undefined;
  readonly matchedTotal: number | null;
}): boolean {
  return input.personalized === true && input.matchedTotal === null;
}

const ISO_DATE_RANGE = /^\d{4}-\d{2}-\d{2}\.\.\d{4}-\d{2}-\d{2}$/;

/**
 * Scope noun for the remainder line. Matches the results-bar noun (WEB-790 /
 * PAR-25) for each named preset. Custom ISO ranges keep their own dates so a
 * 14-day picker is never labeled "tonight". No date filter — the default
 * "all upcoming" state — reads "upcoming" (same wording as the rail caption's
 * `formatDateScopeLabel`), never a fabricated "tonight".
 */
export function formatDiscoverListTailScope(date: DateValue | undefined): string {
  switch (date) {
    case 'tomorrow':
      return 'tomorrow';
    case 'weekend':
      return 'this weekend';
    case 'next7':
      return 'the next 7 days';
    case 'next30':
      return 'the next 30 days';
    case 'tonight':
      return 'tonight';
    case 'this-week':
      return 'this week';
    default: {
      if (typeof date === 'string' && ISO_DATE_RANGE.test(date)) {
        const [start, end] = date.split('..');
        return start === end ? start : `${start} to ${end}`;
      }
      return 'upcoming';
    }
  }
}

export function formatDiscoverListTailRemainderLine(
  remainder: number,
  date: DateValue | undefined,
): string {
  return `${formatCount(remainder)} more in range ${formatDiscoverListTailScope(date)} · ${LIST_TAIL_NO_TASTE_SIGNAL} · ${LIST_TAIL_TIME_ORDER}`;
}

export function formatDiscoverListTailShowAll(total: number): string {
  return `Show all ${formatCount(total)}`;
}

/**
 * Next broader date preset. Named presets: `tonight` / `tomorrow` /
 * `weekend` / unset → `next7`; `next7` → `next30`; `next30` hides the
 * widen block. Custom ISO ranges hide Widen until an anchored superset
 * can be serialized — rolling `next7` / `next30` can drop the selected
 * window (e.g. `2026-08-20..2026-08-26` is not a subset of "next 7 days
 * from today").
 */
export function nextWidenDatePreset(date: DateValue | undefined): 'next7' | 'next30' | null {
  if (date === 'next30') return null;
  if (date === 'next7') return 'next30';
  if (typeof date === 'string' && ISO_DATE_RANGE.test(date)) {
    return null;
  }
  return 'next7';
}

export function formatDiscoverListTailWidenLabel(nextPreset: 'next7' | 'next30'): string {
  return nextPreset === 'next30' ? 'Next 30 days' : 'Next 7 days';
}
